"use client"

import { ScreenNames } from 'config/routing'
import { selectActiveBrandId, useAppSelector } from 'services'

import useDashboardRoutes from '../useDashboardRoutes/useDashboardRoutes'
import useFeatureRoutes from '../useFeatureRoutes/useFeatureRoutes'
import useSalesRoutes from '../useSalesRoutes'

interface IMMBRoutes {
    staticRoutes: {
        [key: string]: string
    }
    currentMMBDashboard: () => string
    mmbDashboard: (brandId: string) => string
    mmbRoot: () => string
    mmbSales: () => string
    branding: () => string
    locations: () => string
    mmbMediaLibrary: () => string
    products: () => string
    events: () => string
    addLocation: () => string
    addProduct: () => string
    editLocation: (locationId: string) => string
    editProduct: (productSlug: string) => string
    editProductById: (productId: string) => string
    requestAccount: () => string
    mmbAnalytics: () => string
    productStyles: () => string
    favorites: () => string
    impressions: () => string
    savedRoutes: () => string
    brewStats: () => string
    mmbSettings: () => string
    mmbWorkWeekAndHours: () => string
    mmbBrandingDetails: () => string
}

//Marketing / MarketMyBrewery
// const MMB_Unsubscribed = `/${ScreenNames.Unsubscribed}`;
const brandingRoute = `${ScreenNames.Branding}`
const locationsRoute = `${ScreenNames.Locations}`
const mmbMediaLibraryRoute = `${ScreenNames.MediaLibrary}`
const productsRoute = `${ScreenNames.Products}`
const eventsRoute = `${ScreenNames.Events}`
const addLocationRoute = `${ScreenNames.Locations}/${ScreenNames.Add}`
const addProductRoute = `${ScreenNames.Products}/${ScreenNames.Add}`
const editLocationRoute = `${ScreenNames.Locations}/`
const editProductRoute = `${ScreenNames.Products}/`
const editProductByIdRoute = `${ScreenNames.Products}/`
const requestAccountRoute = `${ScreenNames.RequestAccount}`

//Marketing Analytics
const mmbAnalyticsRoute = `${ScreenNames.Analytics}`
const productStylesRoute = `${ScreenNames.Analytics}/${ScreenNames.ProductStyles}`
const favoritesRoute = `${ScreenNames.Analytics}/${ScreenNames.Favorites}`
const impressionsRoute = `${ScreenNames.Analytics}/${ScreenNames.Impressions}`
const savedRoutesRoute = `${ScreenNames.Analytics}/${ScreenNames.SavedRoutes}`
const brewStatsMarketingRoute = `${ScreenNames.Analytics}/${ScreenNames.BrewStats}`

//Marketing Settings
const mmbSettingsRoute = `${ScreenNames.Settings}`
const mmbWorkWeekAndHoursRoute = `${ScreenNames.Settings}/${ScreenNames.MMB_WorkWeekAndHours}`
const mmbBrandingDetailsRoute = `${ScreenNames.Settings}/${ScreenNames.BrandingDetails}`

const useMMBRoutes = (): IMMBRoutes => {
    const activeBrandId = useAppSelector(selectActiveBrandId)

    const { mmbDashboard: mmbDashboardRoot } = useDashboardRoutes()
    const { marketMyBreweryFeatureRoute } = useFeatureRoutes()
    const { mmbSales } = useSalesRoutes()

    const mmbSubWrapper = (link) =>
        activeBrandId ? `${marketMyBreweryFeatureRoute()}/${link}` : marketMyBreweryFeatureRoute()

    // Current URLs Root:
    const branding = () => mmbSubWrapper(brandingRoute)
    const locations = () => mmbSubWrapper(locationsRoute)
    const mmbMediaLibrary = () => mmbSubWrapper(mmbMediaLibraryRoute)
    const products = () => mmbSubWrapper(productsRoute)
    const events = () => mmbSubWrapper(eventsRoute)
    const addLocation = () => mmbSubWrapper(addLocationRoute)
    const addProduct = () => mmbSubWrapper(addProductRoute)
    const editLocation = (locationId: string) => mmbSubWrapper(`${editLocationRoute}${locationId}`)
    const editProduct = (productSlug: string) => mmbSubWrapper(`${editProductRoute}${productSlug}`)
    const editProductById = (productId: string) =>
        mmbSubWrapper(`${editProductByIdRoute}${productId}`)
    const requestAccount = () => mmbSubWrapper(requestAccountRoute)

    // Current URLs Analytics
    const mmbAnalytics = () => mmbSubWrapper(mmbAnalyticsRoute)
    const productStyles = () => mmbSubWrapper(productStylesRoute)
    const favorites = () => mmbSubWrapper(favoritesRoute)
    const impressions = () => mmbSubWrapper(impressionsRoute)
    const savedRoutes = () => mmbSubWrapper(savedRoutesRoute)
    const brewStats = () => mmbSubWrapper(brewStatsMarketingRoute)

    // Current URLs Settings
    const mmbSettings = () => mmbSubWrapper(mmbSettingsRoute)
    const mmbWorkWeekAndHours = () => mmbSubWrapper(mmbWorkWeekAndHoursRoute)
    const mmbBrandingDetails = () => mmbSubWrapper(mmbBrandingDetailsRoute)
    const mmbRoot = marketMyBreweryFeatureRoute

    return {
        currentMMBDashboard: mmbDashboardRoot,
        mmbDashboard: mmbDashboardRoot,
        mmbRoot,
        mmbSales,
        branding,
        locations,
        mmbMediaLibrary,
        products,
        events,
        addLocation,
        addProduct,
        editProductById,
        editLocation,
        editProduct,
        requestAccount,
        mmbAnalytics,
        productStyles,
        favorites,
        impressions,
        savedRoutes,
        brewStats,
        mmbSettings,
        mmbWorkWeekAndHours,
        mmbBrandingDetails,
        staticRoutes: {
            brandingStatic: brandingRoute,
            locationsStatic: locationsRoute,
            mmbMediaLibraryStatic: mmbMediaLibraryRoute,
            productsStatic: productsRoute,
            eventsStatic: eventsRoute,
            addLocationStatic: addLocationRoute,
            addProductStatic: addProductRoute,
            editLocationStatic: editLocationRoute,
            editProductById: editProductByIdRoute,
            editProductStatic: editProductRoute,
            requestAccountStatic: requestAccountRoute,
            mmbAnalyticsStatic: mmbAnalyticsRoute,
            productStylesStatic: productStylesRoute,
            favoritesStatic: favoritesRoute,
            impressionsStatic: impressionsRoute,
            savedRoutesStatic: savedRoutesRoute,
            brewStatsMarketingStatic: brewStatsMarketingRoute,
            mmbSettingsStatic: mmbSettingsRoute,
            mmbWorkWeekAndHoursStatic: mmbWorkWeekAndHoursRoute,
            mmbBrandingDetailsStatic: mmbBrandingDetailsRoute
        }
    }
}

export default useMMBRoutes
